/// <reference lib="dom" />
// (rasterizeMask가 DOM 타입을 쓰므로 lib 참조 — DOM lib 없는 소비자(main)의
// typecheck를 깨지 않기 위함. 런타임 DOM 의존은 rasterizeMask 한정.)
import type { SurfaceMask } from '../types.js';

// =============================================================================
// Calibration v2 — 마스크 래스터라이저 (PLAN §4.1).
// 2층 분리: 커맨드 생성(순수 함수, 단위 테스트 대상) / Path2D 렌더(DOM 의존).
// 마스크는 알파 텍스처로 GPU에 올라가 프래그먼트에서 곱해진다 — 정점 수 무제한,
// 베지어·자기교차(별)·invert 전부 canvas 2D가 처리.
// =============================================================================

/** Path2D 호출과 1:1 대응하는 직렬화 가능한 경로 커맨드. */
export type PathCommand =
  | { op: 'moveTo'; x: number; y: number }
  | { op: 'lineTo'; x: number; y: number }
  | {
      op: 'bezierCurveTo';
      c1x: number; c1y: number;
      c2x: number; c2y: number;
      x: number; y: number;
    }
  | { op: 'closePath' };

/**
 * 마스크 폴리곤(+베지어 핸들)을 경로 커맨드 배열로 변환한다. 좌표는 정규화
 * [0,1] 그대로 — 래스터 단계에서 픽셀로 스케일된다.
 *
 * 엣지 i→j 규칙: `vertices[i].h2`(나가는 핸들) 또는 `vertices[j].h1`(들어오는
 * 핸들)이 하나라도 있으면 큐빅 베지어(없는 쪽 핸들은 정점 자체로 퇴화), 둘 다
 * 없으면 직선. 마지막 엣지(vN-1→v0)가 직선이면 closePath 하나로 닫는다.
 * 정점이 3개 미만이면 빈 배열(그릴 것 없음).
 */
export function buildMaskPathCommands(mask: SurfaceMask): PathCommand[] {
  const verts = mask.vertices;
  if (verts.length < 3) return [];
  const first = verts[0]!;
  const commands: PathCommand[] = [{ op: 'moveTo', x: first.p.x, y: first.p.y }];
  for (let i = 0; i < verts.length; i++) {
    const from = verts[i]!;
    const isClosing = i === verts.length - 1;
    const to = isClosing ? first : verts[i + 1]!;
    if (from.h2 || to.h1) {
      const c1 = from.h2 ?? from.p;
      const c2 = to.h1 ?? to.p;
      commands.push({
        op: 'bezierCurveTo',
        c1x: c1.x, c1y: c1.y,
        c2x: c2.x, c2y: c2.y,
        x: to.p.x, y: to.p.y,
      });
    } else if (!isClosing) {
      commands.push({ op: 'lineTo', x: to.p.x, y: to.p.y });
    }
    // 직선 닫힘 엣지는 lineTo 대신 closePath가 처리.
  }
  commands.push({ op: 'closePath' });
  return commands;
}

type Canvas2DContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/**
 * 마스크를 알파 마스크로 래스터한다. 흰색(alpha 1) = 보이는 영역.
 * - fill rule 'evenodd' — 별 등 자기교차 폴리곤 대응 (MadMapper와 동일 동작).
 * - feather: ctx.filter blur(px). 캔버스가 출력 해상도의 1/N일 때는 호출부에서
 *   feather도 같은 비율로 줄여 넘길 것.
 * - invert: 전체를 채운 뒤 도형을 'destination-out'으로 뚫는다.
 *
 * DOM(canvas 2D) 의존 — 단위 테스트 대상 아님. 순수 로직은
 * {@link buildMaskPathCommands}에 있다.
 */
export function rasterizeMask(
  mask: SurfaceMask,
  canvas: HTMLCanvasElement | OffscreenCanvas,
  width: number,
  height: number,
): void {
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d') as Canvas2DContext | null;
  if (!ctx) throw new Error('rasterizeMask: 2D context unavailable');
  ctx.clearRect(0, 0, width, height);

  const commands = buildMaskPathCommands(mask);
  if (commands.length === 0) {
    // 그릴 도형이 없으면 전체 노출 (마스크 없음과 동일).
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    return;
  }

  const path = commandsToPath2D(commands, width, height);
  ctx.save();
  ctx.fillStyle = '#fff';
  if (mask.invert) {
    // 전체를 채운 뒤 도형 안쪽을 뚫는다 — 도형 바깥이 보이는 영역이 된다.
    ctx.fillRect(0, 0, width, height);
    ctx.globalCompositeOperation = 'destination-out';
  }
  if (mask.feather > 0) ctx.filter = `blur(${mask.feather}px)`;
  ctx.fill(path, 'evenodd');
  ctx.restore();
}

/** 정규화 커맨드를 픽셀 공간 Path2D로 변환. */
function commandsToPath2D(commands: PathCommand[], width: number, height: number): Path2D {
  const path = new Path2D();
  for (const cmd of commands) {
    switch (cmd.op) {
      case 'moveTo':
        path.moveTo(cmd.x * width, cmd.y * height);
        break;
      case 'lineTo':
        path.lineTo(cmd.x * width, cmd.y * height);
        break;
      case 'bezierCurveTo':
        path.bezierCurveTo(
          cmd.c1x * width, cmd.c1y * height,
          cmd.c2x * width, cmd.c2y * height,
          cmd.x * width, cmd.y * height,
        );
        break;
      case 'closePath':
        path.closePath();
        break;
    }
  }
  return path;
}
